import { useState, useEffect, useMemo } from 'react';
import { Plus, CreditCard as Edit, Trash2, Save, X, Star, Languages, Code, Sparkles, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { ConfirmModal } from './ConfirmModal';
import { useNotify } from './useNotify';
import { DataTable, type ColumnDef } from './DataTable';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES, computeChanges } from '../lib/auditLog';

interface WorkCodeRow {
  id: string;
  code: string;
  description: string;
  is_default: boolean | null;
  is_issue: boolean | null;
  aspire_catalog_item_id: number | null;
  created_at: string;
}

interface WorkCodeType {
  id: string;
  work_code_id: string;
  type_name: 'Zone' | 'Controller' | 'DoubleCheck';
}

interface WorkCodeTranslationRow {
  id: string;
  work_code_id: string;
  locale: string;
  translated_code: string;
  translated_description: string;
}

interface WorkCode {
  id: string;
  code: string;
  description: string;
  is_default: boolean;
  is_issue: boolean;
  aspire_catalog_item_id: number | null;
  created_at: string;
  types: string[];
  translated_code_es: string;
  translated_description_es: string;
}

const ALL_TYPES = ['Zone', 'Controller', 'DoubleCheck'] as const;

const TYPE_KEY_MAP: Record<string, string> = {
  Zone: 'workCodes.types.zone',
  Controller: 'workCodes.types.controller',
  DoubleCheck: 'workCodes.types.doubleCheck',
};

export function JobCodesManagement() {
  const { t } = useTranslation('common');
  const { notify, NotifyModal } = useNotify();
  const [workCodes, setWorkCodes] = useState<WorkCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; code: string } | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    is_default: false,
    is_issue: true,
    types: ['Zone'] as string[],
    translated_code_es: '',
    translated_description_es: '',
  });
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    void fetchWorkCodes();
  }, []);

  const fetchWorkCodes = async () => {
    setLoading(true);
    try {
      const [codes, types, translations] = await Promise.all([
        apiClient.get<WorkCodeRow[]>('/api/work-codes'),
        apiClient.get<WorkCodeType[]>('/api/work-code-types'),
        apiClient.get<WorkCodeTranslationRow[]>('/api/work-code-translations'),
      ]);

      const typesByCodeId = new Map<string, string[]>();
      for (const wct of types) {
        if (!typesByCodeId.has(wct.work_code_id)) typesByCodeId.set(wct.work_code_id, []);
        typesByCodeId.get(wct.work_code_id)!.push(wct.type_name);
      }

      const translationsByCodeId = new Map<string, WorkCodeTranslationRow>();
      for (const tr of translations) {
        if (tr.locale === 'es') translationsByCodeId.set(tr.work_code_id, tr);
      }

      const sorted = [...codes].sort((a, b) => a.code.localeCompare(b.code));
      const merged: WorkCode[] = sorted.map((wc) => {
        const tr = translationsByCodeId.get(wc.id);
        return {
          ...wc,
          is_default: wc.is_default ?? false,
          is_issue: wc.is_issue ?? false,
          types: typesByCodeId.get(wc.id) || [],
          translated_code_es: tr?.translated_code || '',
          translated_description_es: tr?.translated_description || '',
        };
      });

      setWorkCodes(merged);
    } catch (error) {
      console.error('Error fetching work codes:', error);
      notify(t('status.errorTitle'), t('workCodes.failedToLoad'), 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({ code: '', description: '', is_default: false, is_issue: true, types: ['Zone'], translated_code_es: '', translated_description_es: '' });
  };

  const handleEdit = (workCode: WorkCode) => {
    setEditingId(workCode.id);
    setIsAdding(false);
    setFormData({
      code: workCode.code,
      description: workCode.description,
      is_default: workCode.is_default,
      is_issue: workCode.is_issue,
      types: workCode.types.length > 0 ? [...workCode.types] : ['Zone'],
      translated_code_es: workCode.translated_code_es,
      translated_description_es: workCode.translated_description_es,
    });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ code: '', description: '', is_default: false, is_issue: true, types: ['Zone'], translated_code_es: '', translated_description_es: '' });
  };

  const toggleType = (type: string) => {
    setFormData((prev) => {
      const has = prev.types.includes(type);
      if (has && prev.types.length === 1) return prev;
      return {
        ...prev,
        types: has ? prev.types.filter((tp) => tp !== type) : [...prev.types, type],
      };
    });
  };

  const saveTypes = async (workCodeId: string, types: string[]) => {
    const existing = await apiClient.get<WorkCodeType[]>('/api/work-code-types');
    const mine = existing.filter((wct) => wct.work_code_id === workCodeId);
    await Promise.all(mine.map((wct) => apiClient.del(`/api/work-code-types/${wct.id}`)));
    for (const tp of types) {
      await apiClient.post('/api/work-code-types', { work_code_id: workCodeId, type_name: tp });
    }
  };

  // Calls the server-side Google Translate proxy with the current English
  // code + description and returns the Spanish pair. Empty inputs round-trip
  // as empty strings so the caller can decide whether to overwrite.
  const fetchSpanishTranslations = async (
    code: string,
    description: string,
  ): Promise<{ code: string; description: string }> => {
    const result = await apiClient.post<{ success: boolean; translations?: string[]; error?: string }>(
      '/api/integrations/translate',
      { texts: [code, description], targetLocale: 'es', sourceLocale: 'en' },
    );
    if (!result.success || !result.translations) {
      throw new Error(result.error || 'Translation failed');
    }
    return {
      code: result.translations[0] ?? '',
      description: result.translations[1] ?? '',
    };
  };

  const handleTranslate = async () => {
    const enCode = formData.code.trim();
    const enDesc = formData.description.trim();
    if (!enCode && !enDesc) {
      notify(t('status.noticeTitle'), t('workCodes.fillEnglishToTranslate'), 'info');
      return;
    }
    setTranslating(true);
    try {
      const tr = await fetchSpanishTranslations(enCode, enDesc);
      setFormData((prev) => ({
        ...prev,
        translated_code_es: tr.code,
        translated_description_es: tr.description,
      }));
    } catch (err) {
      console.error('Error translating work code:', err);
      const msg = err instanceof Error ? err.message : '';
      const failedMsg = msg ? `${t('workCodes.translationFailed')}: ${msg}` : t('workCodes.translationFailed');
      notify(t('status.errorTitle'), failedMsg, 'danger');
    } finally {
      setTranslating(false);
    }
  };

  const saveTranslation = async (
    workCodeId: string,
    overrides?: { translated_code_es: string; translated_description_es: string },
  ) => {
    const existingAll = await apiClient.get<WorkCodeTranslationRow[]>('/api/work-code-translations');
    const existing = existingAll.find((tr) => tr.work_code_id === workCodeId && tr.locale === 'es');
    const tCode = (overrides?.translated_code_es ?? formData.translated_code_es).trim();
    const tDesc = (overrides?.translated_description_es ?? formData.translated_description_es).trim();
    const wantsRow = tCode || tDesc;

    if (wantsRow) {
      const body = {
        work_code_id: workCodeId,
        locale: 'es',
        translated_code: tCode,
        translated_description: tDesc,
      };
      if (existing) {
        await apiClient.put(`/api/work-code-translations/${existing.id}`, body);
      } else {
        await apiClient.post('/api/work-code-translations', body);
      }
    } else if (existing) {
      await apiClient.del(`/api/work-code-translations/${existing.id}`);
    }
  };

  const handleSave = async () => {
    if (!formData.code.trim() || !formData.description.trim()) {
      notify(t('status.noticeTitle'), t('workCodes.fillAllFields'), 'warning');
      return;
    }
    if (formData.types.length === 0) {
      notify(t('status.noticeTitle'), t('workCodes.selectAtLeastOneType'), 'warning');
      return;
    }

    try {
      // Auto-fill Spanish from the English fields when both Spanish inputs are
      // blank. We do this before the create/update calls so the new row + its
      // translation persist atomically (from the user's POV).
      let translationOverrides: { translated_code_es: string; translated_description_es: string } | undefined;
      if (
        !formData.translated_code_es.trim() &&
        !formData.translated_description_es.trim() &&
        (formData.code.trim() || formData.description.trim())
      ) {
        try {
          setTranslating(true);
          const tr = await fetchSpanishTranslations(formData.code.trim(), formData.description.trim());
          translationOverrides = {
            translated_code_es: tr.code,
            translated_description_es: tr.description,
          };
          setFormData((prev) => ({
            ...prev,
            translated_code_es: tr.code,
            translated_description_es: tr.description,
          }));
        } catch (err) {
          console.warn('Auto-translation skipped:', err);
        } finally {
          setTranslating(false);
        }
      }

      if (isAdding) {
        const created = await apiClient.post<WorkCodeRow>('/api/work-codes', {
          code: formData.code,
          description: formData.description,
          is_default: formData.is_default,
          is_issue: formData.is_issue,
        });
        await saveTypes(created.id, formData.types);
        await saveTranslation(created.id, translationOverrides);
        await logAudit({
          action: AUDIT_ACTIONS.CREATE,
          entityType: AUDIT_ENTITIES.WORK_CODE,
          entityId: created.id,
          entityLabel: formData.code,
          details: { description: formData.description, types: formData.types, is_default: formData.is_default, is_issue: formData.is_issue },
          pageContext: 'job-codes',
        });
      } else if (editingId) {
        await apiClient.put(`/api/work-codes/${editingId}`, {
          code: formData.code,
          description: formData.description,
          is_default: formData.is_default,
          is_issue: formData.is_issue,
        });
        await saveTypes(editingId, formData.types);
        await saveTranslation(editingId, translationOverrides);
        const orig = workCodes.find((wc) => wc.id === editingId);
        await logAudit({
          action: AUDIT_ACTIONS.UPDATE,
          entityType: AUDIT_ENTITIES.WORK_CODE,
          entityId: editingId,
          entityLabel: formData.code,
          details: computeChanges(
            { code: orig?.code, description: orig?.description, is_default: orig?.is_default, is_issue: orig?.is_issue, types: orig?.types },
            { code: formData.code, description: formData.description, is_default: formData.is_default, is_issue: formData.is_issue, types: formData.types },
          ),
          pageContext: 'job-codes',
        });
      }

      await fetchWorkCodes();
      handleCancel();
    } catch (err) {
      console.error('Error saving work code:', err);
      notify(t('status.errorTitle'), t('workCodes.failedToSave'), 'danger');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await apiClient.del(`/api/work-codes/${deleteConfirm.id}`);
      await logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.WORK_CODE,
        entityId: deleteConfirm.id,
        entityLabel: deleteConfirm.code,
        pageContext: 'job-codes',
      });
      setDeleteConfirm(null);
      await fetchWorkCodes();
    } catch (err) {
      console.error('Error deleting work code:', err);
      notify(t('status.errorTitle'), t('workCodes.failedToDelete'), 'danger');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await apiClient.post(`/api/work-codes/${id}/set-default`);
      const wc = workCodes.find((w) => w.id === id);
      await logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.WORK_CODE,
        entityId: id,
        entityLabel: wc?.code || '',
        details: { is_default: true },
        pageContext: 'job-codes',
      });
      await fetchWorkCodes();
    } catch (err) {
      console.error('Error setting default work code:', err);
      notify(t('status.errorTitle'), t('workCodes.failedToSetDefault'), 'danger');
    }
  };

  const typeBadge = (type: string) => {
    const colors: Record<string, string> = {
      Controller: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
      DoubleCheck: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
      Zone: 'bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-300',
    };
    return (
      <span key={type} className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[type] || 'bg-gray-100 text-gray-800'}`}>{/* theme-allow: neutral fallback pill for unknown type (part of type color map) */}
        {t(TYPE_KEY_MAP[type] || type)}
      </span>
    );
  };

  const typeCheckboxes = (selected: string[], onToggle: (type: string) => void, idPrefix: string) => (
    <div className="flex flex-wrap gap-3">
      {ALL_TYPES.map((type) => {
        const checked = selected.includes(type);
        return (
          <label key={type} className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(type)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              id={`${idPrefix}_${type}`}
            />
            <span className="text-sm text-theme-primary">{t(TYPE_KEY_MAP[type])}</span>
          </label>
        );
      })}
    </div>
  );

  const columns: ColumnDef<WorkCode>[] = useMemo(() => [
    {
      key: 'code',
      label: t('workCodes.code'),
      render: (wc) => (
        <span className="text-sm font-medium text-theme-primary">{wc.code}</span>
      ),
    },
    {
      key: 'description',
      label: t('workCodes.description'),
      render: (wc) => (
        <span className="text-sm text-theme-secondary truncate block max-w-[200px]">{wc.description}</span>
      ),
    },
    {
      key: 'translation',
      label: t('workCodes.spanishTranslation'),
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: (wc) => wc.translated_code_es ? (
        <span className="text-sm text-theme-secondary">{wc.translated_code_es}</span>
      ) : (
        <span className="text-xs text-theme-secondary italic">-</span>
      ),
    },
    {
      key: 'types',
      label: t('workCodes.appliesTo'),
      headerClassName: 'hidden md:table-cell',
      cellClassName: 'hidden md:table-cell',
      render: (wc) => (
        <div className="flex flex-wrap gap-1">
          {wc.types.length > 0
            ? wc.types.map((tp) => typeBadge(tp))
            : <span className="text-xs text-theme-secondary">{t('workCodes.none')}</span>}
        </div>
      ),
    },
    {
      key: 'is_default',
      label: t('workCodes.default'),
      headerClassName: 'text-center hidden sm:table-cell',
      cellClassName: 'text-center hidden sm:table-cell',
      render: (wc) => (
        <button
          onClick={(e) => { e.stopPropagation(); if (!wc.is_default) void handleSetDefault(wc.id); }}
          className={`p-1 rounded transition-colors ${wc.is_default ? 'text-yellow-500' : 'text-theme-secondary hover:text-yellow-500'}`}
          title={wc.is_default ? t('workCodes.defaultCode') : t('workCodes.setAsDefault')}
          disabled={wc.is_default}
        >
          <Star size={16} fill={wc.is_default ? 'currentColor' : 'none'} />
        </button>
      ),
    },
    {
      key: 'is_issue',
      label: t('workCodes.isIssue'),
      headerClassName: 'text-center hidden sm:table-cell',
      cellClassName: 'text-center hidden sm:table-cell',
      render: (wc) => (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
          wc.is_issue ? 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300' : 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300'
        }`}>
          {wc.is_issue ? t('workCodes.yes') : t('workCodes.no')}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      headerClassName: 'w-20',
      render: (wc) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleEdit(wc)}
            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-md transition-colors"
            title={t('actions.edit')}
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => setDeleteConfirm({ id: wc.id, code: wc.code })}
            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors"
            title={t('actions.delete')}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ),
    },
  ], [t, workCodes]);

  const renderExpandedRow = (wc: WorkCode) => {
    if (editingId !== wc.id) return null;
    return (
      <div className="bg-subtle rounded-lg border border-line p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-theme-primary">{t('workCodes.editCode')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-theme-primary mb-1">{t('workCodes.codeRequired')}</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-theme-primary mb-1">{t('workCodes.descriptionRequired')}</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-theme-primary mb-1">{t('workCodes.appliesToRequired')}</label>
          {typeCheckboxes(formData.types, toggleType, `edit_${wc.id}`)}
        </div>
        <div className="border-t border-blue-200 pt-3">
          <div className="flex items-center gap-2 mb-2">
            <Languages size={14} className="text-blue-600" />
            <span className="text-xs font-medium text-theme-primary">{t('workCodes.spanishTranslation')}</span>
            <button
              type="button"
              onClick={handleTranslate}
              disabled={translating || (!formData.code.trim() && !formData.description.trim())}
              className="p-1 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-500/20 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
              title={t('workCodes.translateFromEnglish')}
              aria-label={t('workCodes.translateFromEnglish')}
            >
              {translating ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-theme-primary mb-1">{t('workCodes.translatedCode')}</label>
              <input
                type="text"
                value={formData.translated_code_es}
                onChange={(e) => setFormData({ ...formData, translated_code_es: e.target.value })}
                placeholder={t('workCodes.translatedCodePlaceholder')}
                className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-theme-primary mb-1">{t('workCodes.translatedDescription')}</label>
              <input
                type="text"
                value={formData.translated_description_es}
                onChange={(e) => setFormData({ ...formData, translated_description_es: e.target.value })}
                placeholder={t('workCodes.translatedDescriptionPlaceholder')}
                className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`is_default_edit_${wc.id}`}
              checked={formData.is_default}
              onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor={`is_default_edit_${wc.id}`} className="text-sm text-theme-primary">{t('workCodes.setAsDefaultCode')}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`is_issue_edit_${wc.id}`}
              checked={formData.is_issue}
              onChange={(e) => setFormData({ ...formData, is_issue: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor={`is_issue_edit_${wc.id}`} className="text-sm text-theme-primary">{t('workCodes.representsIssue')}</label>
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <Save size={16} />
            {t('actions.save')}
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 px-4 py-2 bg-subtle text-theme-primary rounded-lg hover:bg-subtle transition-colors text-sm font-medium"
          >
            <X size={16} />
            {t('actions.cancel')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <h2 className="text-2xl font-bold text-theme-primary">{t('workCodes.title')}</h2>
          <p className="text-sm text-theme-secondary mt-1">{t('workCodes.subtitle')}</p>
        </div>
        {!isAdding && (
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <Plus size={18} />
            {t('workCodes.addCode')}
          </button>
        )}
      </div>

      {isAdding && (
        <div className="mb-3 p-4 bg-subtle rounded-lg border border-line flex-shrink-0">
          <h3 className="text-sm font-semibold text-theme-primary mb-3">{t('workCodes.addNew')}</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-theme-primary mb-1">{t('workCodes.codeRequired')}</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder={t('workCodes.codePlaceholder')}
                  className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-theme-primary mb-1">{t('workCodes.descriptionRequired')}</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('workCodes.descriptionPlaceholder')}
                  className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-theme-primary mb-1">{t('workCodes.appliesToRequired')}</label>
              {typeCheckboxes(formData.types, toggleType, 'new')}
            </div>
            <div className="border-t border-line pt-3">
              <div className="flex items-center gap-2 mb-2">
                <Languages size={14} className="text-blue-600" />
                <span className="text-xs font-medium text-theme-primary">{t('workCodes.spanishTranslation')}</span>
                <button
                  type="button"
                  onClick={handleTranslate}
                  disabled={translating || (!formData.code.trim() && !formData.description.trim())}
                  className="p-1 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-500/20 rounded transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                  title={t('workCodes.translateFromEnglish')}
                  aria-label={t('workCodes.translateFromEnglish')}
                >
                  {translating ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-theme-primary mb-1">{t('workCodes.translatedCode')}</label>
                  <input
                    type="text"
                    value={formData.translated_code_es}
                    onChange={(e) => setFormData({ ...formData, translated_code_es: e.target.value })}
                    placeholder={t('workCodes.translatedCodePlaceholder')}
                    className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-theme-primary mb-1">{t('workCodes.translatedDescription')}</label>
                  <input
                    type="text"
                    value={formData.translated_description_es}
                    onChange={(e) => setFormData({ ...formData, translated_description_es: e.target.value })}
                    placeholder={t('workCodes.translatedDescriptionPlaceholder')}
                    className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default_new"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_default_new" className="text-sm text-theme-primary">{t('workCodes.setAsDefaultCode')}</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_issue_new"
                  checked={formData.is_issue}
                  onChange={(e) => setFormData({ ...formData, is_issue: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_issue_new" className="text-sm text-theme-primary">{t('workCodes.representsIssue')}</label>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <Save size={16} />
                {t('actions.save')}
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 bg-subtle text-theme-primary rounded-lg hover:bg-subtle transition-colors text-sm font-medium"
              >
                <X size={16} />
                {t('actions.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      <DataTable
        data={workCodes}
        columns={columns}
        keyField="id"
        loading={loading}
        emptyIcon={<Code size={40} className="text-theme-secondary" />}
        emptyTitle={t('workCodes.noWorkCodes')}
        onRowClick={(wc) => {
          if (editingId === wc.id) {
            handleCancel();
          } else {
            handleEdit(wc);
          }
        }}
        expandedId={editingId}
        expandedRow={renderExpandedRow}
      />

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        title={t('workCodes.deleteTitle')}
        message={t('workCodes.confirmDelete', { code: deleteConfirm?.code })}
        confirmText={t('actions.delete')}
        cancelText={t('actions.cancel')}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
      />

      {NotifyModal}
    </div>
  );
}
